/**
 * Prepares engine SVG for innerHTML.
 * - Defence in depth: the engine may echo pattern text (possibly from a shared link) into the SVG.
 *   Keeps plain SVG and its <style>, drops scripts, event handlers, foreign content, external refs.
 * - Normalizes size: engine maps may carry only a viewBox; width/height are derived from it so the
 *   map has an intrinsic size inside the zoom/pan viewport.
 */
const FORBIDDEN = new Set(['script', 'foreignobject', 'iframe', 'object', 'embed', 'animate', 'set', 'animatemotion', 'animatetransform', 'image']);

function cleanCss(css: string): string {
  return css.replace(/@import[^;]*;?/gi, '').replace(/url\(\s*(['"]?)(?!#)[^)]*\1\s*\)/gi, 'none').replace(/expression\s*\(/gi, '(');
}

export function sanitizeSvg(svg: string): string | null {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length > 0) return null;
  const walk = (el: Element) => {
    for (const child of Array.from(el.children)) {
      if (FORBIDDEN.has(child.localName.toLowerCase())) {
        child.remove();
        continue;
      }
      if (child.localName.toLowerCase() === 'style') {
        child.textContent = cleanCss(child.textContent ?? '');
        continue;
      }
      walk(child);
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      else if ((name === 'href' || name === 'xlink:href') && !value.startsWith('#')) el.removeAttribute(attr.name);
      else if (value.includes('javascript:')) el.removeAttribute(attr.name);
      else if (name === 'style') el.setAttribute(attr.name, cleanCss(attr.value));
    }
  };
  walk(root);
  // The viewport around the map carries a label in the UI language; the engine's own (French) goes.
  for (const title of Array.from(root.getElementsByTagName('title'))) title.remove();
  root.removeAttribute('aria-label');
  root.removeAttribute('role');
  root.setAttribute('aria-hidden', 'true');
  if (!root.getAttribute('width') || !root.getAttribute('height')) {
    const vb = (root.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
    if (vb.length === 4 && vb.every(Number.isFinite) && vb[2] > 0 && vb[3] > 0) {
      root.setAttribute('width', String(vb[2]));
      root.setAttribute('height', String(vb[3]));
    }
  }
  return new XMLSerializer().serializeToString(root);
}
